export type ParsedBinding = {
  key: string;
  ctrl: boolean;
  meta: boolean;
  alt: boolean;
  shift: boolean;
  /** When true, the binding fires with or without Shift held. Use for
   *  letter bindings where the capital form is the same intent (e.g. `r`
   *  and `R` both mean "focus reply"). Caller must guarantee no conflicting
   *  `shift+<same-key>` binding exists in the registry. */
  shiftOptional?: boolean;
};

const isMac = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const platform = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform
    || navigator.platform
    || '';
  return /Mac|iPhone|iPad|iPod/i.test(platform);
};

export const IS_MAC = isMac();

export function parseBinding(binding: string): ParsedBinding {
  const parts = binding.toLowerCase().split('+').map(p => p.trim()).filter(Boolean);
  const parsed: ParsedBinding = { key: '', ctrl: false, meta: false, alt: false, shift: false };
  for (const part of parts) {
    if (part === 'mod') {
      if (IS_MAC) parsed.meta = true;
      else parsed.ctrl = true;
    } else if (part === 'ctrl' || part === 'control') {
      parsed.ctrl = true;
    } else if (part === 'meta' || part === 'cmd' || part === 'command') {
      parsed.meta = true;
    } else if (part === 'alt' || part === 'option' || part === 'opt') {
      parsed.alt = true;
    } else if (part === 'shift') {
      parsed.shift = true;
    } else {
      parsed.key = part;
    }
  }
  return parsed;
}

// Map physical key codes to logical key names for fallback matching.
// On macOS, Option (Alt) remaps event.key to alternate characters (e.g. ⌥+1 → ¡),
// so we need event.code to identify the physical key when Alt is held.
const CODE_TO_KEY: Record<string, string> = {
  Digit0: '0', Digit1: '1', Digit2: '2', Digit3: '3', Digit4: '4',
  Digit5: '5', Digit6: '6', Digit7: '7', Digit8: '8', Digit9: '9',
  KeyA: 'a', KeyB: 'b', KeyC: 'c', KeyD: 'd', KeyE: 'e', KeyF: 'f',
  KeyG: 'g', KeyH: 'h', KeyI: 'i', KeyJ: 'j', KeyK: 'k', KeyL: 'l',
  KeyM: 'm', KeyN: 'n', KeyO: 'o', KeyP: 'p', KeyQ: 'q', KeyR: 'r',
  KeyS: 's', KeyT: 't', KeyU: 'u', KeyV: 'v', KeyW: 'w', KeyX: 'x',
  KeyY: 'y', KeyZ: 'z',
  BracketLeft: '[', BracketRight: ']', Slash: '/',
  Enter: 'enter', Escape: 'escape',
  ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down',
};

export function matchesBinding(event: KeyboardEvent, parsed: ParsedBinding): boolean {
  if (parsed.ctrl !== event.ctrlKey) return false;
  if (parsed.meta !== event.metaKey) return false;
  if (parsed.alt !== event.altKey) return false;
  // `?` is a shifted character on most layouts; don't enforce shift for it.
  // `shiftOptional` bindings (e.g. `r` for focus-reply) intentionally accept
  // both lowercase and shift+letter without a separate registry entry.
  const shiftSensitive = parsed.key !== '?' && !parsed.shiftOptional;
  if (shiftSensitive && parsed.shift !== event.shiftKey) return false;
  // KeyboardEvent.key can be undefined for some synthetic / IME events
  // even though the spec says string. No-match is the safe answer; the
  // alternative is a crash that breaks the global keydown handler.
  if (typeof event.key !== 'string') return false;
  const eventKey = event.key.toLowerCase();
  // On macOS, Alt remaps event.key; fall back to event.code for the physical key.
  const codeKey = event.code ? CODE_TO_KEY[event.code] : undefined;
  if (parsed.key === 'enter') return eventKey === 'enter' || codeKey === 'enter';
  if (parsed.key === 'escape' || parsed.key === 'esc') return eventKey === 'escape' || codeKey === 'escape';
  if (parsed.key === 'left') return eventKey === 'arrowleft' || codeKey === 'left';
  if (parsed.key === 'right') return eventKey === 'arrowright' || codeKey === 'right';
  if (parsed.key === 'up') return eventKey === 'arrowup' || codeKey === 'up';
  if (parsed.key === 'down') return eventKey === 'arrowdown' || codeKey === 'down';
  if (parsed.key === '?') return eventKey === '?' || (eventKey === '/' && event.shiftKey) || (codeKey === '/' && event.shiftKey);
  return eventKey === parsed.key || codeKey === parsed.key;
}

export function formatBinding(binding: string): string[] {
  const parsed = parseBinding(binding);
  const parts: string[] = [];
  if (parsed.ctrl) parts.push(IS_MAC ? '⌃' : 'Ctrl');
  if (parsed.alt) parts.push(IS_MAC ? '⌥' : 'Alt');
  if (parsed.shift) parts.push(IS_MAC ? '⇧' : 'Shift');
  if (parsed.meta) parts.push(IS_MAC ? '⌘' : 'Win');
  if (parsed.key) {
    const keyLabel: Record<string, string> = {
      enter: '↵',
      escape: 'Esc',
      left: '←',
      right: '→',
      up: '↑',
      down: '↓',
    };
    parts.push(keyLabel[parsed.key] ?? parsed.key.toUpperCase());
  }
  return parts;
}
